import React from "react";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Luxuriya Homes - Data Security & Trust</title>
        <meta 
          name="description" 
          content="Learn how Luxuriya Homes protects your personal data. We prioritize your privacy and ensure your information is used solely for delivering world-class interior designs." 
        />
        <meta 
          name="keywords" 
          content="Privacy Policy, Data Security, Luxuriya Homes Privacy, Interior Design Data Protection" 
        />
        <link rel="canonical" href="https://www.luxuriyahomes.com/privacy-policy" />
      </Helmet>

      <div className="bg-neutral-50 min-h-screen text-neutral-800 font-sans">
        
        {/* HERO / HEADER */}
        <header
          className="relative h-[50vh] md:h-[65vh] flex items-center justify-center text-white"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=2000&auto=format&fit=crop')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-black/70"></div>

          {/* Content */}
          <div className="relative z-10 text-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif tracking-widest text-[#D4AF37] mb-4">
              Privacy Policy
            </h1>

            <div className="w-24 md:w-32 h-[2px] bg-[#D4AF37] mx-auto mt-6 md:mt-8 mb-8 md:mb-10"></div>

            <p className="max-w-xl md:max-w-2xl mx-auto text-neutral-300 text-base md:text-lg leading-relaxed px-2">
              Your trust and data security are our priority. Learn how we handle your information here.
            </p>
          </div>
        </header>

        {/* CONTENT */}
        <main className="max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20">
          
          {/* INTRO */}
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20">
            <p className="text-base md:text-lg leading-loose text-neutral-600">
              At{" "}
              <span className="font-semibold text-black">
                Luxuriya Homes
              </span>, 
              we respect your privacy. We ensure that your personal data remains safe 
              and is used solely to provide you with better interior design services.
            </p>
          </div>

          {/* SECTIONS */}
          <div className="space-y-8 md:space-y-12">
            <Section title="1. Information Collection">
              To maintain service quality, we collect some basic information:
              <ul className="list-disc pl-5 md:pl-6 space-y-2 mt-4">
                 <li><strong>Personal Details:</strong> Name, Email, Phone Number, and Address.</li>
                 <li><strong>Project Details:</strong> Floor plans, site photos, and design preferences.</li>
                 <li><strong>Technical Data:</strong> Cookies and IP address to improve website experience.</li>
              </ul>
            </Section>

            <Section title="2. Use of Information">
              Your data is used only for the purposes listed below:
              <ul className="list-disc pl-5 md:pl-6 space-y-2 mt-4">
                <li>For the planning and execution of your project.</li>
                <li>To send you design updates and quotations.</li>
                <li>To resolve customer support issues and queries.</li>
                <li>We do not sell your data to any irrelevant third parties.</li>
              </ul>
            </Section>

            <Section title="3. Data Sharing & Disclosure">
              We keep your information strictly confidential. It is shared only when:
              <ul className="list-disc pl-5 md:pl-6 space-y-2 mt-4">
                <li>Trusted vendors/contractors are required for project execution.</li>
                <li>Required by legal compliance or government authorities.</li>
                <li>Secure gateways are being used for payment processing.</li>
              </ul>
            </Section>

            <Section title="4. Data Security">
              We use industry-standard security measures and secure servers to protect your personal data from unauthorized access, alteration, or leakage.
            </Section>

            <Section title="5. Your Rights">
              You can request to review, update, or delete your information at any time. If you do not wish to receive marketing communications, you can easily opt-out.
            </Section>
          </div>

          {/* FOOTER NOTE */}
          <div className="mt-16 md:mt-24 pt-10 border-t border-neutral-200 text-center text-sm text-neutral-500">
            <p>Last Updated: January 2026</p>
            <p className="mt-3">
              For privacy-related queries, please contact:{" "}
              <a
                href="mailto:privacy@luxuriyahomes.com"
                className="text-[#D4AF37] hover:underline"
              >
                privacy@luxuriyahomes.com
              </a>
            </p>
          </div>
        </main>
      </div>
    </>
  );
};

/* SECTION COMPONENT */
type SectionProps = {
  title: string;
  children: React.ReactNode;
};

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <section className="bg-white rounded-xl p-6 md:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.06)] border-l-4 border-[#D4AF37]">
    <h2 className="text-xl md:text-3xl font-serif text-black mb-4 md:mb-5">
      {title}
    </h2>
    <div className="text-neutral-600 leading-relaxed text-sm md:text-[15px]">
      {children}
    </div>
  </section>
);

export default PrivacyPolicy;